class TpsLookBook extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.pages = [];
    this.totalPages = 0;
    this.initialized = false;
    this.readyToAnnounce = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.init();
  }

  init() {
    this.isMobile = window.innerWidth <= 768;
    const layoutSelector = this.isMobile ? '.mobile-book-layout' : '.pc-book-layout';
    this.bookContainer = this.querySelector(layoutSelector);
    this.hiddenLayout = this.querySelector(this.isMobile ? '.pc-book-layout' : '.mobile-book-layout');

    if (!this.bookContainer) return;

    this.pages = Array.from(this.bookContainer.querySelectorAll('.page'));
    this.totalPages = this.pages.length;

    this.prevButton = this.querySelector('.btn-prev');
    this.nextButton = this.querySelector('.btn-next');
    this.currentEl = this.querySelector('.page-current');
    this.liveEl = this.querySelector('[data-look-book-live]');
    this.wrapper = this.querySelector('.tps-look-book-wrapper');

    if (this.hiddenLayout) {
      this.hiddenLayout.setAttribute('aria-hidden', 'true');
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.flipPrev());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.flipNext());
    }

    this.addEventListener('keydown', (event) => this.handleKeydown(event));

    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;

      if (wasMobile !== this.isMobile) {
        location.reload();
        return;
      }

      this.updateState();
    });

    this.pages.forEach((page, index) => {
      page.addEventListener('click', (e) => {
        if (e.target.closest('.tps-look-book-tag')) return;

        if (!this.isMobile) {
          if (index === this.currentIndex) {
            this.flipNext();
          } else if (index === this.currentIndex - 1) {
            this.flipPrev();
          }
        }
      });
    });

    this.updateState();
    this.readyToAnnounce = true;

    this.loader = this.querySelector('.tps-look-book-loader');
    if (document.readyState === 'complete') {
      this.hideLoader();
    } else {
      window.addEventListener('load', () => this.hideLoader());
      setTimeout(() => this.hideLoader(), 5000);
    }
  }

  handleKeydown(event) {
    if (event.target !== this.prevButton && event.target !== this.nextButton) return;
    if (event.target.closest('a.tps-look-book-tag')) return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.flipNext();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.flipPrev();
    }
  }

  getMaxIndex() {
    return this.isMobile ? this.totalPages - 1 : this.totalPages;
  }

  hideLoader() {
    this.setAttribute('aria-busy', 'false');
    if (this.loader && !this.loader.classList.contains('hidden')) {
      this.loader.classList.add('hidden');
      this.loader.setAttribute('aria-hidden', 'true');
    }
  }

  flipNext() {
    if (this.isMobile) {
      if (this.currentIndex < this.totalPages - 1) {
        const page = this.pages[this.currentIndex];
        if (page) {
          const speed = parseFloat(getComputedStyle(this).getPropertyValue('--tps-book-speed')) * 1000 || 800;

          this.classList.add('is-flipping-next');
          page.classList.add('just-flipped');
          page.classList.add('flipped');
          page.style.zIndex = this.totalPages + 5;

          setTimeout(() => {
            this.classList.remove('is-flipping-next');
            page.classList.remove('just-flipped');
            this.updateState();
          }, speed);
        }
        this.currentIndex++;
        this.updateNavButtons();
        this.updateVisibleFaces();
        this.announcePage();
      }
      return;
    }

    if (this.currentIndex < this.totalPages) {
      const page = this.pages[this.currentIndex];
      if (page) {
        page.classList.add('flipped');
        const speed = parseFloat(getComputedStyle(this).getPropertyValue('--tps-book-speed')) * 1000 || 800;

        this.currentIndex++;
        this.updateState();
        page.style.zIndex = this.totalPages + 50;

        setTimeout(() => {
          page.style.zIndex = '';
          this.updateState();
        }, speed);
      }
    }
  }

  flipPrev() {
    if (this.isMobile) {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        const page = this.pages[this.currentIndex];
        if (page) {
          const speed = parseFloat(getComputedStyle(this).getPropertyValue('--tps-book-speed')) * 1000 || 800;

          this.classList.add('is-flipping-prev');
          page.classList.remove('is-hidden');
          page.classList.add('just-unflipped');
          page.classList.remove('flipped');
          page.style.zIndex = this.totalPages + 5;
          page.style.visibility = 'visible';
          page.style.opacity = '1';

          setTimeout(() => {
            this.classList.remove('is-flipping-prev');
            page.classList.remove('just-unflipped');
            page.style.visibility = '';
            page.style.opacity = '';
            this.updateState();
          }, speed);
        }
        this.updateNavButtons();
        this.updateVisibleFaces();
        this.announcePage();
      }
      return;
    }

    if (this.currentIndex > 0) {
      this.currentIndex--;
      const page = this.pages[this.currentIndex];
      if (page) {
        page.classList.remove('flipped');
        const speed = parseFloat(getComputedStyle(this).getPropertyValue('--tps-book-speed')) * 1000 || 800;

        this.updateState();
        page.style.zIndex = this.totalPages + 50;

        setTimeout(() => {
          page.style.zIndex = '';
          this.updateState();
        }, speed);
      }
    }
  }

  getPageAnnouncement() {
    const total = this.isMobile ? this.totalPages : this.totalPages * 2;
    if (this.isMobile) {
      return `Page ${this.currentIndex + 1} of ${total}`;
    }
    if (this.currentIndex === 0) {
      return `Page 1 of ${total}`;
    }
    if (this.currentIndex === this.totalPages) {
      return `Page ${total} of ${total}`;
    }
    const leftPage = this.currentIndex * 2;
    const rightPage = leftPage + 1;
    return `Pages ${leftPage} to ${rightPage} of ${total}`;
  }

  announcePage() {
    if (!this.liveEl || !this.readyToAnnounce) return;
    const message = this.getPageAnnouncement();
    if (this.liveEl.textContent === message) return;
    this.liveEl.textContent = message;
  }

  setFaceA11y(face, visible) {
    if (!face) return;
    const focusable = face.querySelectorAll('a.tps-look-book-tag');
    if (visible) {
      face.removeAttribute('aria-hidden');
      focusable.forEach((el) => el.removeAttribute('tabindex'));
    } else {
      face.setAttribute('aria-hidden', 'true');
      focusable.forEach((el) => el.setAttribute('tabindex', '-1'));
    }
  }

  updateVisibleFaces() {
    this.pages.forEach((page, index) => {
      const front = page.querySelector('.front');
      const back = page.querySelector('.back');
      let frontVisible = false;
      let backVisible = false;

      if (this.isMobile) {
        frontVisible = index === this.currentIndex;
      } else if (this.currentIndex === 0) {
        frontVisible = index === 0;
      } else if (this.currentIndex === this.totalPages) {
        backVisible = index === this.totalPages - 1;
      } else {
        backVisible = index === this.currentIndex - 1;
        frontVisible = index === this.currentIndex;
      }

      this.setFaceA11y(front, frontVisible);
      this.setFaceA11y(back, backVisible);
    });
  }

  updateNavButtons() {
    if (this.prevButton) {
      const hidePrev = this.currentIndex === 0;
      this.prevButton.style.display = hidePrev ? 'none' : 'flex';
      this.prevButton.setAttribute('aria-hidden', hidePrev ? 'true' : 'false');
    }
    if (this.nextButton) {
      const hideNext = this.currentIndex === this.getMaxIndex();
      this.nextButton.style.display = hideNext ? 'none' : 'flex';
      this.nextButton.setAttribute('aria-hidden', hideNext ? 'true' : 'false');
    }
    if (this.currentEl) {
      const total = this.isMobile ? this.totalPages : this.totalPages * 2;
      this.currentEl.innerText = this.isMobile
        ? `${this.currentIndex + 1} / ${total}`
        : this.getPCPageString();
    }
  }

  getPCPageString() {
    const totalPages = this.totalPages * 2;
    if (this.currentIndex === 0) {
      return `1 / ${totalPages}`;
    } else if (this.currentIndex === this.totalPages) {
      return `${totalPages} / ${totalPages}`;
    } else {
      const leftPage = this.currentIndex * 2;
      const rightPage = leftPage + 1;
      return `${leftPage} - ${rightPage} / ${totalPages}`;
    }
  }

  updateState() {
    if (!this.bookContainer) return;

    this.updateNavButtons();
    this.updateVisibleFaces();
    this.announcePage();

    if (this.isMobile) {
      this.pages.forEach((page, index) => {
        if (index < this.currentIndex) {
          page.style.zIndex = index;
          page.classList.add('is-hidden');
        } else if (index === this.currentIndex) {
          page.style.zIndex = this.totalPages + 1;
          page.classList.remove('is-hidden');
        } else {
          page.style.zIndex = this.totalPages - index;
          page.classList.remove('is-hidden');
        }
      });
    } else {
      this.bookContainer.style.transform = '';

      this.pages.forEach((page, index) => {
        if (index < this.currentIndex) {
          page.style.zIndex = index + 1;
        } else {
          page.style.zIndex = this.totalPages - index + 2;
        }
      });

      if (this.currentIndex > 0 && this.currentIndex < this.totalPages) {
        this.wrapper.classList.add('is-open');
        this.wrapper.classList.remove('is-closed-back');
      } else if (this.currentIndex === this.totalPages) {
        this.wrapper.classList.remove('is-open');
        this.wrapper.classList.add('is-closed-back');
      } else {
        this.wrapper.classList.remove('is-open');
        this.wrapper.classList.remove('is-closed-back');
      }

      if (this.currentEl) {
        this.currentEl.innerText = this.getPCPageString();
      }
    }
  }
}

if (!customElements.get('tps-look-book')) {
  customElements.define('tps-look-book', TpsLookBook);
}
